import { BE_FORM_DATE_FORMAT } from "@farzoom/common-ui-components";
import { useEffect, useState } from "react";
import moment from "moment";
import { debounce } from "lodash";
import { useWatch } from "react-hook-form";
import { SelectBankInfo, selectBanks } from "@services/bankApi";
import { useAuthContext } from "@app/authContext";

const DEBOUNCE_MS = 400;

function getDurationInclusive(
  start?: string,
  end?: string,
  format = BE_FORM_DATE_FORMAT
): number | undefined {
  if (!start || !end) return undefined;

  const s = moment(start, format, true);
  const e = moment(end, format, true);

  if (!s.isValid() || !e.isValid()) return undefined;

  s.startOf("day");
  e.startOf("day");

  // diff не включает конечный день → +1
  return e.diff(s, "days") + 1;
}

export const useBankSelect = ({ methods }) => {
  const { auth } = useAuthContext();
    const accessToken = auth?.access_token ?? "";
  const { control, setValue } = methods;
  const [banks, setBanks] = useState<SelectBankInfo[]>([]);
  const [selectedBanks, setSelectedBanks] = useState<Set<string>>(new Set([]));

  const [principal, guarantee, purchase] = useWatch({
    control: control,
    name: ["principal", "guarantee", "purchase"],
  });

  useEffect(() => {
    if (!principal?.inn) return;

    const fetchBanks = async () => {
      const duration = getDurationInclusive(
        guarantee?.startDate as string | undefined,
        guarantee?.endDate as string | undefined,
        BE_FORM_DATE_FORMAT
      );

      const bankList = await selectBanks(
        {
          principalInn: principal.inn as string,
          beneficiaryInn: purchase?.lots?.[0]?.beneficiaries?.[0]?.inn as string,
          law: purchase?.law as string,
          duration,
          bgAmount: guarantee?.amount as number,
          bgType: guarantee?.type as string,
        },
        accessToken
      );

      setBanks(bankList);

      const succeedBanks = bankList.filter((b) => Boolean(b.terms.result)).map((b) => b.inn);
      handleChangeSelectedBanks(succeedBanks);
    };

    const debounced = debounce(fetchBanks, DEBOUNCE_MS);
    debounced();

    return () => {
      debounced.cancel();
    };
  }, [
    principal?.inn,
    guarantee?.type,
    guarantee?.amount,
    guarantee?.startDate,
    guarantee?.endDate,
    purchase?.law,
    purchase?.lots?.[0]?.beneficiaries?.[0]?.inn,
  ]);

  const handleChangeSelectedBanks = (inn: string | string[]) => {
    if (Array.isArray(inn)) {
      setSelectedBanks(new Set(inn));
      setValue("requestedBanks", inn);
      return;
    }

    setSelectedBanks((prev) => {
      if (prev.has(inn)) {
        prev.delete(inn);
      } else {
        prev.add(inn);
      }

      setValue("requestedBanks", Array.from(prev));
      return new Set(prev);
    });
  };

  return {
    banks,
    selectedBanks,
    onSelectBank: handleChangeSelectedBanks,
  };
};
